/**
 * @file TaskDetailHeaderEdit.jsx
 * @location src/components/modals/progmodal/TaskDetailHeaderEdit.jsx
 * @role 업무 상세 매니저 인라인 편집 헤더 — TaskDetailHeader 의 편집 variant
 *
 * editable=true 일 때 TaskDetailHeader 에서 분기되어 표시.
 * 제목/기한/긴급도/담당/설명을 그 자리에서 편집 → EDIT_TASK dispatch.
 * 상태는 담당자 워크플로 — 여기서는 읽기 전용.
 */
import { useState, useRef, useMemo } from 'react';
import { useApp } from '../../../store/AppContext';
import { useMembers } from '../../../store/useMembers';
import { ackStatusMeta, getInitials, kubunLabel } from '../../../data/constants';
import { fmtShort } from '../../../utils/dateUtils';
import { CustomSelect } from '../../ui/SharedUI';
import { Icon } from '../../ui/Icon';
import { PriorityButtons } from '../taskform/PriorityButtons';
import { duePresetOptions, openNativeDatePicker } from '../taskform/duePicker';

/**
 * @param {{ task: import('../../../data/types.js').Task }} props
 */
export function TaskDetailHeaderEdit({ task }) {
  const { S, act } = useApp();
  const { mrole } = useMembers();
  const sm = ackStatusMeta(task.status, task.acknowledgedAt);

  const [title, setTitle] = useState(task.title);
  const [note,  setNote]  = useState(task.note ?? '');

  const edit = (/** @type {object} */ changes) => act('EDIT_TASK', { id: task.id, changes });

  // 기한 picker
  const dateRef = useRef(/** @type {HTMLInputElement|null} */ (null));

  // 담당자 옵션 (EditModal aeeOpts 패턴)
  const aeeOpts = useMemo(() => S.members.map(m => ({
    value: m.id,
    label: m.name,
    sub: kubunLabel(m.isManager),
    avatar: m.isManager ? 'bucho' : 'staff',
    avatarText: getInitials(m.name),
  })), [S.members]);

  // 제목 commit on blur or Enter
  const commitTitle = () => {
    const v = title.trim();
    if (v && v !== task.title) edit({ title: v });
    else if (!v) setTitle(task.title); // restore if empty
  };

  // 설명 commit on blur
  const commitNote = () => {
    if (note !== (task.note ?? '')) edit({ note });
  };

  return (
    <div className="td-head td-head-edit">
      {/* 제목 */}
      <input
        className="finput td-title-input"
        value={title}
        onChange={e => setTitle(e.target.value)}
        onBlur={commitTitle}
        onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); e.currentTarget.blur(); } }}
        aria-label="업무 제목"
      />

      <div className="td-info">
        {/* 상태 — 읽기 전용 */}
        <div className="td-row">
          <span className="td-k">상태</span>
          <span className="td-v"><span className={`tag ${sm.cls}`}>{sm.label}</span></span>
        </div>

        {/* 기한 / 지정일 */}
        <div className="td-row td-row-edit">
          <span className="td-k">{task.dateKind === 'ondate' ? '지정일' : '기한'}</span>
          <div className="td-v td-due-edit">
            <div className="recur-card recur-card-sm">
              <div className="recur-card-head">
                {task.due && <span className="due-head-val">{fmtShort(task.due)}</span>}
                <span className="due-cal">
                  <button type="button" className="due-cal-btn" onClick={() => openNativeDatePicker(dateRef.current)} aria-label="날짜 선택">
                    <Icon name="calendar" size={14} />
                  </button>
                  <input
                    ref={dateRef}
                    type="date"
                    className="dpick-native"
                    value={task.due ?? ''}
                    onChange={e => edit({ due: e.target.value || null })}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                </span>
              </div>
              <div className="due-quick">
                {duePresetOptions().map(([lbl, val]) => (
                  <button key={lbl} type="button" className={`due-chip${task.due === val ? ' active' : ''}`}
                    onClick={() => edit({ due: val })}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 긴급도 */}
        <div className="td-row td-row-edit">
          <span className="td-k">긴급도</span>
          <div className="td-v">
            <PriorityButtons value={task.priority} onChange={v => edit({ priority: v })} compact />
          </div>
        </div>

        {/* 담당 */}
        <div className="td-row td-row-edit">
          <span className="td-k">담당</span>
          <div className="td-v">
            <CustomSelect
              value={task.aee}
              onChange={v => edit({ aee: v })}
              options={aeeOpts}
            />
            {task.aor && <span className="td-sub">← {mrole(task.aor)} {S.members.find(m => m.id === task.aor)?.name ?? ''}</span>}
          </div>
        </div>

        {/* 설명 */}
        <div className="td-row td-row-edit">
          <span className="td-k">설명</span>
          <textarea
            className="finput td-note-ta"
            rows={2}
            value={note}
            onChange={e => setNote(e.target.value)}
            onBlur={commitNote}
            placeholder="업무의 보조 설명 (선택)"
          />
        </div>
      </div>
    </div>
  );
}
